import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useIsMobile } from '../hooks/useIsMobile';
import logo from '../assets/logo.svg';

const linkStyle = {
  fontFamily: "'Josefin Sans', sans-serif",
  fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase',
  color: '#111', textDecoration: 'none', fontWeight: 300,
  transition: 'opacity 0.2s', borderBottom: '1px solid transparent',
};

export default function Navbar() {
  const { count } = useCart();
  /* Hamburger kicks in at 1100px — wide enough to prevent logo overlap on small laptops */
  const isMobile = useIsMobile(1100);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hidden, setHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const lastY = useRef(0);
  const navigate = useNavigate();
  const navH = isMobile ? 70 : 100;

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setHidden(y > lastY.current && y > 80);
      lastY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleSearch = (e) => {
    if (e.key === 'Enter' && query.trim()) {
      navigate(`/catalogue?q=${encodeURIComponent(query)}`);
      setSearchOpen(false);
      setQuery('');
    }
  };

  return (
    <>
      <div style={{ height: `${navH}px` }} />
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: '#fff', borderBottom: '1px solid #e8e8e8',
        /* 3-column flex: left | logo | right — logo stays centered regardless of screen width */
        display: 'flex', alignItems: 'center',
        padding: isMobile ? '0 20px' : '0 clamp(24px, 6vw, 96px)',
        height: `${navH}px`,
        transition: 'transform 0.35s ease',
        transform: hidden ? 'translateY(-100%)' : 'translateY(0)',
      }}>
        {/* Left */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '32px' }}>
          {isMobile ? (
            <button
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Menu"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', display: 'flex', flexDirection: 'column', gap: '5px' }}
            >
              <span style={{ display: 'block', width: '22px', height: '1px', background: '#111', transition: 'all 0.3s', transform: menuOpen ? 'rotate(45deg) translate(4px, 4px)' : 'none' }} />
              <span style={{ display: 'block', width: '22px', height: '1px', background: '#111', transition: 'all 0.3s', opacity: menuOpen ? 0 : 1 }} />
              <span style={{ display: 'block', width: '22px', height: '1px', background: '#111', transition: 'all 0.3s', transform: menuOpen ? 'rotate(-45deg) translate(4px, -4px)' : 'none' }} />
            </button>
          ) : (
            <>
              <Link to="/" style={linkStyle}>Accueil</Link>
              <Link to="/catalogue" style={linkStyle}>Catalogue</Link>
              <Link to="/contact" style={linkStyle}>Contact</Link>
            </>
          )}
        </div>

        {/* Center: logo — always centered between the two flex:1 sides */}
        <Link to="/">
          <img src={logo} alt="Norelio" style={{ height: isMobile ? '22px' : '28px', display: 'block' }} />
        </Link>

        {/* Right */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: isMobile ? '12px' : '20px' }}>
          {!isMobile && (
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }} onClick={() => setSearchOpen(s => !s)} aria-label="Recherche">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.5">
                <circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="22" y2="22"/>
              </svg>
            </button>
          )}
          <button
            style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            onClick={() => navigate('/panier')}
            aria-label="Panier"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.5">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 01-8 0"/>
            </svg>
            {count > 0 && (
              <span style={{ position: 'absolute', top: '-6px', right: '-8px', background: '#111', color: '#fff', borderRadius: '50%', width: '16px', height: '16px', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {count}
              </span>
            )}
          </button>
        </div>

        {searchOpen && !isMobile && (
          <input
            autoFocus
            style={{ position: 'absolute', top: `${navH}px`, left: 0, right: 0, background: '#fff', borderBottom: '1px solid #e8e8e8', padding: '12px 40px', fontFamily: "'Josefin Sans', sans-serif", fontSize: '13px', border: 'none', outline: 'none', letterSpacing: '1px' }}
            placeholder="Rechercher un produit..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleSearch}
          />
        )}
      </nav>

      {/* Mobile full-screen menu */}
      {isMobile && menuOpen && (
        <div style={{
          position: 'fixed', top: `${navH}px`, left: 0, right: 0, bottom: 0,
          background: '#fff', zIndex: 99,
          display: 'flex', flexDirection: 'column',
          padding: '40px 32px',
        }}>
          {[['Accueil', '/'], ['Catalogue', '/catalogue'], ['Contact', '/contact'], ['Panier', '/panier']].map(([label, path]) => (
            <Link key={path} to={path} onClick={() => setMenuOpen(false)} style={{
              fontFamily: "'Josefin Sans', sans-serif",
              fontSize: '13px', letterSpacing: '3px', textTransform: 'uppercase',
              color: '#111', textDecoration: 'none', fontWeight: 300,
              padding: '20px 0', borderBottom: '1px solid #f0f0f0',
            }}>{label}</Link>
          ))}
        </div>
      )}
    </>
  );
}
